import { motion } from "framer-motion";

const domains = [
  { n: "01", title: "Self Discovery", color: "var(--brand)" },
  { n: "02", title: "Emotional Intelligence", color: "var(--sunflower)" },
  { n: "03", title: "Confidence & Voice", color: "var(--violet)" },
  { n: "04", title: "Navigating Influences", color: "var(--mint)" },
  { n: "05", title: "Resilience & Grit", color: "var(--brand)" },
  { n: "06", title: "Decision Making", color: "var(--sunflower)" },
  { n: "07", title: "Self Worth", color: "var(--violet)" },
  { n: "08", title: "Purpose & Direction", color: "var(--mint)" },
];

export default function SpiPsyModel() {
  return (
    <section id="model" className="paper-bg py-24 md:py-40 relative overflow-hidden" data-testid="model-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 relative">
        <div className="mb-16 md:mb-20">
          <h2 className="font-display text-5xl md:text-7xl lg:text-[7.2rem] leading-[0.92] tracking-tight text-[color:var(--ink)]">
              The <em className="font-serif-editorial" style={{ color: "var(--brand)" }}>SPI-PSY</em> Model.
              <br />
              <span className="italic font-serif-editorial" style={{ color: "var(--ink)" }}>
                Spiritual
              </span>{" "}
              <span className="text-[color:var(--muted)]">·</span>{" "}
              <span className="italic font-serif-editorial" style={{ color: "var(--violet)" }}>
                Psychology
              </span>{" "}
              <span className="text-[color:var(--muted)]">·</span>{" "}
              <span className="italic font-serif-editorial" style={{ color: "var(--mint)" }}>
                Integrated
              </span>
          </h2>
        </div>

        {/* Definitions row — Spirituality (left) · Psychology (right) */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 mb-16 md:mb-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="md:col-span-6 relative border-2 p-8 md:p-10 min-h-[280px] flex flex-col justify-end"
            style={{ borderColor: "var(--ink)", background: "var(--violet)", color: "#fff" }}
            data-testid="model-card-spi"
          >
            <div>
              <h3 className="font-display text-4xl md:text-5xl leading-[1.02] tracking-tight">Spirituality</h3>
              <p className="mt-4 text-white/90 leading-relaxed max-w-[52ch]">
                A deep sense of connection to something larger than oneself; the search for meaning and purpose; the inward journey to understand one&rsquo;s true nature. It emphasises compassion, interconnectedness, and inner peace.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.08 }}
            className="md:col-span-6 relative border-2 p-8 md:p-10 min-h-[280px] flex flex-col justify-end"
            style={{ borderColor: "var(--ink)", background: "var(--sunflower)" }}
            data-testid="model-card-psy"
          >
            <div>
              <h3 className="font-display text-4xl md:text-5xl leading-[1.02] tracking-tight text-[color:var(--ink)]">
                Psychology
              </h3>
              <p className="mt-4 text-[color:var(--ink)]/80 leading-relaxed max-w-[46ch]">
                The scientific study of the human mind and its functions — especially those affecting behaviour in a given context.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Bridge */}
        <div className="text-center mb-16 md:mb-24" data-testid="model-bridge">
          <span className="font-mono-editorial text-[10px] tracking-[0.35em] text-[color:var(--muted)]">
            SPI &nbsp;+&nbsp; PSY &nbsp;=&nbsp;
          </span>
          <h3 className="mt-4 font-display italic text-5xl md:text-8xl leading-[0.95] tracking-tight text-[color:var(--ink)]">
            Inner <span style={{ color: "var(--brand)" }}>Compass</span>.
          </h3>
          <p className="mt-6 text-[color:var(--ink)]/70 max-w-[42ch] mx-auto">
            Not therapy. Not tutoring. A structured program — building inner strength <em>before</em> problems arise.
          </p>
        </div>

        {/* 8 Domains */}
        <div className="grid grid-cols-12 gap-6 mb-10">
          <div className="col-span-12 md:col-span-4">
            <span className="font-mono-editorial text-[11px] text-[color:var(--muted)]">— The Eight Pillars</span>
            <h3 className="mt-4 font-display text-4xl md:text-5xl leading-[1] tracking-tight">
              What we <em style={{ color: "var(--brand)" }}>build</em> in a year.
            </h3>
            <p className="mt-6 text-[color:var(--ink)]/70 max-w-[38ch] leading-relaxed">
              Every session touches at least one. Over a season, the eight pillars form a compass — measurable, private, and yours.
            </p>
          </div>

          <div className="col-span-12 md:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-testid="domains-grid">
            {domains.map((d, i) => (
              <motion.div
                key={d.n}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                whileHover={{ y: -4 }}
                className="relative aspect-square border-2 p-5 flex flex-col justify-between transition-colors duration-500 cursor-default"
                style={{ borderColor: "var(--ink)", background: d.color }}
                data-testid={`domain-${d.n}`}
              >
                <span className="font-serif-editorial italic text-2xl text-[color:var(--ink)]">{d.n}</span>
                <span className="font-display text-xl md:text-2xl leading-[1.05] tracking-tight text-[color:var(--ink)]">
                  {d.title}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
